import React, { useEffect } from 'react';
import { DEBOUNCE_DURATION, RoutePath, SPACING } from '../../generic/config';
import Searcher from '../../generic/Searcher';
import { Context } from '../../App';
import { debounce } from 'lodash';
import { QueryKeys } from '../../api/base/react-query';
import { getUsers } from '../../api/user-controller';
import { useQuery } from 'react-query';
import Table, { TableProps } from '../../base-components/Table';
import { useNavigate } from 'react-router-dom';
import EditorParent from '@/base-components/EditorParent';
import { User } from '@/model/user';
import { Button } from '@/components/ui/button';
import { Translations } from '@/generic/Translations';

const UserSearchPage: React.FC = () => {
    const { translations } = React.useContext(Context);
    const navigate = useNavigate();

    const usersQuery = useQuery<User[]>(QueryKeys.users, getUsers);

    const [filter, setFilter] = React.useState("");

    useEffect(() => {
        usersQuery.refetch();
    }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = debounce((value: string) => {
        setFilter(value);
    }, DEBOUNCE_DURATION);

    const filteredUsers = (usersQuery.data ?? []).filter(user => {
        if (!filter) return true;
        const lowerFilter = filter.toLowerCase();
        return (
            user.username?.toLowerCase().includes(lowerFilter) ||
            user.firstName?.toLowerCase().includes(lowerFilter) ||
            user.lastName?.toLowerCase().includes(lowerFilter) ||
            user.email?.toLowerCase().includes(lowerFilter)
        );
    });

    const tableProps: TableProps<User> = {
        columns: [
            { title: translations.model.user.id, scale: 1, getter: (r) => r.id },
            { title: translations.model.user.username, scale: 2, getter: (r) => r.username },
            { title: translations.model.user.name, scale: 2, getter: (r) => `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim() },
            { title: translations.model.user.email, scale: 2, getter: (r) => r.email ?? "" },
            { title: translations.model.user.phone, scale: 2, getter: (r) => r.phone ?? "" },
            { title: translations.model.user.enabled, scale: 1, getter: (r) => r.enabled ? "✓" : "✗" },
        ],
        rows: filteredUsers,
        isLoading: usersQuery.isLoading,
        href: (row) => `${RoutePath.Users}/${row.id}`,
    };

    return (
        <EditorParent title={translations.model.user.search.title} description={translations.model.user.search.description}>
            <Searcher {...translations.model.user.search} onChange={(value: string) => handleChange(value)}>
                <Button my={SPACING} onClick={() => navigate(`${RoutePath.Users}/new`)}>{translations.common.registerNew}</Button>
            </Searcher>
            <Table tableProps={tableProps} />
        </EditorParent>
    );
};

export default UserSearchPage;
